import { useEffect, useMemo, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { num } from "@/lib/format";
import { quoteToForm, quoteToItems, quoteToResult, isMultiQuote } from "@/lib/quoteEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CarrierComparison from "@/components/workspace/CarrierComparison";
import FinancialBreakdown from "@/components/workspace/FinancialBreakdown";
import RouteMap from "@/components/workspace/RouteMap";
import ExecutiveOverview from "@/components/workspace/ExecutiveOverview";
import CustomerSummary from "@/components/workspace/CustomerSummary";
import DiagnosticsDrawer from "@/components/workspace/DiagnosticsDrawer";
import EmailQuoteDialog from "@/components/workspace/EmailQuoteDialog";
import AddressBookDialog from "@/components/workspace/AddressBookDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useZipAutofill } from "@/lib/useZipAutofill";
import {
  Trash2, Plus, ArrowLeftRight, Package, BookOpen, Truck, Loader2, FileDown,
  Mail, AlertTriangle, MapPin, Sparkles, Layers, FilePlus2,
} from "lucide-react";

const LTL_KEY = "phs.ws.multi.quoteId";

const HANDLING_UNITS = ["Pallet", "Skid", "Crate", "Box", "Carton", "Drum", "Bundle", "Roll", "Tote", "Bag", "Loose"];
const CLASSES = ["50", "55", "60", "65", "70", "77.5", "85", "92.5", "100", "110", "125", "150", "175", "200", "250", "300", "400", "500"];

const PICKUP_ACC = [
  ["residential_pickup", "Residential Pickup"],
  ["liftgate_pickup", "Lift Gate Pickup"],
  ["limited_access_pickup", "Limited Access Pickup"],
];
const DEST_ACC = [
  ["appointment_required", "Delivery Appointment"],
  ["residential_delivery", "Residential Delivery"],
  ["notify_before_delivery", "Notify Consignee"],
  ["limited_access_delivery", "Limited Access Delivery"],
  ["liftgate_delivery", "Lift Gate Delivery"],
  ["inside_delivery", "Inside Delivery"],
  ["hazmat", "Hazardous Material"],
  ["over_dimensional", "Over-Dimensional"],
];

const newItem = (mode = "manual") => ({
  _key: Math.random().toString(36).slice(2),
  mode,
  product_id: "",
  quantity: 1,
  units: 1,
  handling_unit_type: "Pallet",
  pieces: 1,
  weight: "",
  weight_is_total: false,
  length: "",
  width: "",
  height: "",
  freight_class: "",
  nmfc: "",
  description: "",
  stackable: false,
  hazmat: false,
  used: false,
  machinery: false,
});

const initialForm = {
  customer_name: "", quote_ref: "", customer_type: "standard",
  shipping_mode: "LTL", fedex_service_type: "FEDEX_FREIGHT_PRIORITY",
  declared_value: 0, pickup_date: "", origin_zip: "", destination_zip: "",
  origin_city: "", origin_state: "", dest_city: "", dest_state: "",
  pickup_location_type: "commercial", delivery_location_type: "commercial", notes: "",
  customer_address_id: "", dest_company: "", dest_address1: "", dest_contact_name: "", dest_contact_phone: "", dest_contact_email: "",
  origin_company: "", origin_address1: "", origin_contact_name: "", origin_contact_phone: "",
  accessorials: {
    liftgate_pickup: false, liftgate_delivery: false, residential_pickup: false,
    residential_delivery: false, limited_access_pickup: false, limited_access_delivery: false,
    appointment_required: false, inside_delivery: false, notify_before_delivery: false,
    hazmat: false, over_dimensional: false,
  },
};

function Chip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
          : "border-border bg-card text-muted-foreground hover:bg-secondary"
      }`}
    >
      {label}
    </button>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function deriveFinancial(rate, override) {
  if (!rate) return null;
  const base = { ...rate.pricing };
  if (override.price !== null && override.price !== "" && !isNaN(override.price)) {
    const sell = Number(override.price);
    base.sell_price = sell;
    base.is_override = true;
    base.margin_dollars = +(sell - rate.total_carrier_cost).toFixed(2);
    base.margin_pct = sell > 0 ? +((base.margin_dollars / sell) * 100).toFixed(2) : 0;
    base.margin_health = base.margin_pct >= 30 ? "green" : base.margin_pct >= base.min_margin_pct ? "yellow" : "red";
  }
  return base;
}

export default function LtlWorkspace({ products = [], salesRep = "", editQuote = null }) {
  const [form, setForm] = useState(initialForm);
  const [items, setItems] = useState([newItem("manual")]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [override, setOverride] = useState({ price: null, reason: "" });
  const [manualRate, setManualRate] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [explainText, setExplainText] = useState("");
  const [addrBook, setAddrBook] = useState({ open: false, side: null });
  const [pendingRate, setPendingRate] = useState(false);
  const appliedEditId = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAcc = (k) => setForm((f) => ({ ...f, accessorials: { ...f.accessorials, [k]: !f.accessorials[k] } }));
  const zipFill = useZipAutofill(setForm);

  const [shipOrigins, setShipOrigins] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/ship-origins", { params: { active_only: true } })
      .then((r) => { if (alive) setShipOrigins(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const selectShipFrom = (id) => {
    const o = shipOrigins.find((x) => x.id === id);
    if (!o) return;
    setForm((f) => ({
      ...f,
      ship_origin_id: o.id,
      origin_zip: o.zip || f.origin_zip,
      origin_city: o.city || f.origin_city,
      origin_state: o.state || f.origin_state,
      origin_company: o.company || o.label || f.origin_company,
      origin_address1: o.address1 || f.origin_address1,
      origin_contact_name: o.contact_name || f.origin_contact_name,
      origin_contact_phone: o.contact_phone || f.origin_contact_phone,
    }));
  };

  const applyAddress = (addr) => {
    if (addrBook.side === "pickup") {
      setForm((f) => ({
        ...f,
        origin_zip: addr.zip || "",
        origin_city: addr.city || "",
        origin_state: addr.state || "",
        pickup_location_type: addr.location_type || "commercial",
        origin_company: addr.company || "",
        origin_address1: addr.address1 || "",
        origin_contact_name: addr.contact_name || "",
        origin_contact_phone: addr.contact_phone || "",
      }));
    } else {
      setForm((f) => ({
        ...f,
        destination_zip: addr.zip || "",
        dest_city: addr.city || "",
        dest_state: addr.state || "",
        delivery_location_type: addr.location_type || "commercial",
        customer_address_id: addr.id || "",
        dest_company: addr.company || "",
        dest_address1: addr.address1 || "",
        dest_contact_name: addr.contact_name || "",
        dest_contact_phone: addr.contact_phone || "",
        dest_contact_email: addr.contact_email || "",
        customer_name: f.customer_name || addr.company || "",
      }));
    }
  };
  const addrPrefill = () =>
    addrBook.side === "pickup"
      ? { company: form.customer_name, zip: form.origin_zip, city: form.origin_city, state: form.origin_state, location_type: form.pickup_location_type }
      : { company: form.customer_name, zip: form.destination_zip, city: form.dest_city, state: form.dest_state, location_type: form.delivery_location_type };

  const updateItem = (key, patch) => setItems((arr) => arr.map((it) => (it._key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((arr) => (arr.length > 1 ? arr.filter((it) => it._key !== key) : arr));
  const addItem = (mode) => setItems((arr) => [...arr, newItem(mode)]);

  const swapEnds = () =>
    setForm((f) => ({
      ...f,
      origin_zip: f.destination_zip, destination_zip: f.origin_zip,
      origin_city: f.dest_city, dest_city: f.origin_city,
      origin_state: f.dest_state, dest_state: f.origin_state,
    }));

  // Local totals preview (before rating)
  const localTotals = useMemo(() => {
    let weight = 0, units = 0;
    for (const it of items) {
      const u = Number(it.units) || 0;
      if (it.mode === "manual") {
        const w = Number(it.weight) || 0;
        weight += it.weight_is_total ? w : w * (u || 1);
        units += u || 0;
      } else {
        const p = products.find((x) => x.id === it.product_id);
        if (p) {
          const qty = Number(it.quantity) || 0;
          weight += qty * (Number(p.weight) || 0);
          units += qty; // rough; backend computes real stacks
        }
      }
    }
    return { weight: Math.round(weight * 100) / 100, units };
  }, [items, products]);

  const selectedRate = useMemo(
    () => result?.rates.find((r) => r.carrier_code === selectedCode) || null,
    [result, selectedCode],
  );
  const financial = useMemo(() => deriveFinancial(selectedRate, override), [selectedRate, override]);

  const payloadItems = () =>
    items.map((it) => ({
      mode: it.mode,
      product_id: it.product_id || null,
      quantity: Number(it.quantity) || 1,
      units: Number(it.units) || 1,
      handling_unit_type: it.handling_unit_type,
      pieces: Number(it.pieces) || 1,
      weight: Number(it.weight) || 0,
      weight_is_total: it.weight_is_total,
      length: Number(it.length) || 0,
      width: Number(it.width) || 0,
      height: Number(it.height) || 0,
      freight_class: it.freight_class || "",
      nmfc: it.nmfc || "",
      description: it.description || "",
      stackable: it.stackable, hazmat: it.hazmat, used: it.used, machinery: it.machinery,
    }));

  const basePayload = () => ({
    ...form,
    declared_value: Number(form.declared_value) || 0,
    manual_rate: manualRate ? Number(manualRate) : null,
    line_items: payloadItems(),
  });

  const getRates = async (opts = {}) => {
    if (items.some((it) => it.mode === "catalog" && !it.product_id))
      return toast.error("Pick a catalog product for every catalog line, or switch it to Manual.");
    setLoading(true);
    try {
      const isNew = !savedQuote;
      const { data } = await api.post("/quotes/rate-multi", {
        ...basePayload(),
        sales_rep: form.sales_rep || salesRep,
        quote_id: savedQuote?.id || null,
        selected_carrier_code: opts.preferCarrier || null,
      });
      setResult(data);
      if (data.quote) {
        setSavedQuote(data.quote);
        sessionStorage.setItem(LTL_KEY, data.quote.id);
      }
      const keep = opts.preferCarrier && data.rates?.some((r) => r.carrier_code === opts.preferCarrier);
      setSelectedCode(keep ? opts.preferCarrier : data.recommended_carrier_code || data.rates[0]?.carrier_code);
      setOverride({ price: null, reason: "" });
      const errs = (data.warnings || []).filter((w) => w.level === "error");
      if (errs.length) toast.warning(`${errs.length} blocking issue(s) — review below.`);
      else if (data.quote) toast.success(isNew ? `Rates calculated — saved as ${data.quote.quote_number}.` : `Rates updated on ${data.quote.quote_number}.`);
      else toast.success("Rates calculated.");
      if (isNew && data.quote) api.get("/motivation/hype?event=save").then((r) => { if (r.data?.text) toast(r.data.text); }).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Restore the last auto-saved LTL quote when returning to the workspace (no re-rating).
  useEffect(() => {
    if (editQuote) return;
    const storedId = sessionStorage.getItem(LTL_KEY);
    if (!storedId) return;
    api.get(`/quotes/${storedId}`).then(({ data }) => {
      if (!isMultiQuote(data)) return;
      setForm((f) => ({ ...f, ...quoteToForm(data), accessorials: { ...f.accessorials, ...(data.accessorials || {}) } }));
      const its = quoteToItems(data);
      if (its.length) setItems(its);
      setResult(quoteToResult(data));
      setSavedQuote(data);
      setSelectedCode(data.selected_carrier_code || null);
      setManualRate(data.manual_rate != null ? String(data.manual_rate) : "");
    }).catch(() => sessionStorage.removeItem(LTL_KEY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a saved multi-item quote for re-editing, then auto-rate once.
  useEffect(() => {
    if (!editQuote || appliedEditId.current === editQuote.id) return;
    appliedEditId.current = editQuote.id;
    setForm((f) => ({ ...f, ...quoteToForm(editQuote), accessorials: { ...f.accessorials, ...(editQuote.accessorials || {}) } }));
    const its = quoteToItems(editQuote);
    if (its.length) setItems(its);
    setSavedQuote(editQuote);
    setSelectedCode(editQuote.selected_carrier_code || null);
    setManualRate(editQuote.manual_rate != null ? String(editQuote.manual_rate) : "");
    sessionStorage.setItem(LTL_KEY, editQuote.id);
    setPendingRate(true);
  }, [editQuote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingRate) return;
    setPendingRate(false);
    getRates({ preferCarrier: editQuote?.selected_carrier_code });
  }, [pendingRate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rating auto-saves the quote; Save now just confirms carrier selection, overrides & header edits.
  const saveQuote = async () => {
    if (!result || !savedQuote) return toast.error("Get rates first.");
    if (override.price !== null && override.price !== "" && !override.reason.trim())
      return toast.error("A reason is required when overriding the price.");
    setSaving(true);
    try {
      const { data } = await api.put(`/quotes/${savedQuote.id}`, {
        selected_carrier_code: selectedCode,
        override_price: override.price !== null && override.price !== "" ? Number(override.price) : null,
        override_reason: override.reason,
        status: "quoted",
        customer_name: form.customer_name,
        quote_ref: form.quote_ref,
        sales_rep: form.sales_rep || salesRep,
        notes: form.notes,
        customer_address_id: form.customer_address_id,
        dest_company: form.dest_company,
        dest_address1: form.dest_address1,
        dest_contact_name: form.dest_contact_name,
        dest_contact_phone: form.dest_contact_phone,
        dest_contact_email: form.dest_contact_email,
        origin_company: form.origin_company,
        origin_address1: form.origin_address1,
        origin_contact_name: form.origin_contact_name,
        origin_contact_phone: form.origin_contact_phone,
      });
      setSavedQuote(data);
      toast.success(`Quote ${data.quote_number} updated.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const newQuote = () => {
    sessionStorage.removeItem(LTL_KEY);
    setForm(initialForm);
    setItems([newItem("manual")]);
    setResult(null);
    setSelectedCode(null);
    setOverride({ price: null, reason: "" });
    setManualRate("");
    setSavedQuote(null);
    toast.info("Started a new quote.");
  };

  const duplicateQuote = async () => {
    if (!savedQuote) return;
    try {
      const { data } = await api.post(`/quotes/${savedQuote.id}/duplicate`);
      setSavedQuote(data);
      sessionStorage.setItem(LTL_KEY, data.id);
      toast.success(`Duplicated as ${data.quote_number}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const explain = async () => {
    if (!result || !selectedRate) return toast.error("Get rates first.");
    setShowExplain(true);
    setExplaining(true);
    setExplainText("");
    try {
      const { data } = await api.post("/quotes/explain", {
        stack_result: result.stack_result,
        fak: result.fak,
        financial,
        recommended: selectedRate,
      });
      setExplainText(data.explanation);
    } catch (e) {
      setExplainText(apiError(e));
    } finally {
      setExplaining(false);
    }
  };

  const downloadPdf = async () => {
    if (!savedQuote) return;
    try {
      const res = await api.get(`/quotes/${savedQuote.id}/pdf?copy=customer`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${savedQuote.quote_number}_customer.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      toast.success(`Downloaded ${savedQuote.quote_number}_customer.pdf`);
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 4000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const errors = (result?.warnings || []).filter((w) => w.level === "error");
  const warns = (result?.warnings || []).filter((w) => w.level !== "error");

  return (
    <div className="space-y-5 animate-fade-up" data-testid="ltl-workspace">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-baseline gap-2">
            <h1 className="font-heading text-2xl font-black tracking-tighter">{savedQuote ? `Editing ${savedQuote.quote_number}:` : "New Quote:"}</h1>
            <input
              data-testid="ltl-customer-name"
              value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value.toUpperCase())}
              placeholder="CUSTOMER NAME"
              className="border-b border-dashed border-border bg-transparent text-2xl font-black tracking-tighter text-[hsl(var(--primary))] outline-none focus:border-[hsl(var(--primary))]"
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Sales rep: <span className="font-medium text-foreground">{salesRep || "—"}</span>
            <span className="mx-2">·</span>Mode: {form.shipping_mode}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Field label="Reference">
            <Input data-testid="ltl-quote-ref" value={form.quote_ref} onChange={(e) => set("quote_ref", e.target.value)} className="h-9 w-40" placeholder="PO / Ref #" />
          </Field>
          <Button data-testid="ltl-new-quote" variant="outline" onClick={newQuote} className="h-9 gap-1.5">
            <FilePlus2 className="h-4 w-4" /> New Quote
          </Button>
        </div>
      </div>

      {/* Pickup + Destination */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr]">
        {/* Pickup */}
        <div className="overflow-hidden border border-border bg-card">
          <div className="flex items-center justify-between bg-[hsl(var(--primary))]/15 px-4 py-2 text-sm font-bold uppercase tracking-wider text-[hsl(var(--primary))]">
            <span>Pickup</span>
            <button data-testid="ltl-addressbook-pickup" onClick={() => setAddrBook({ open: true, side: "pickup" })} className="flex items-center gap-1 rounded px-2 py-1 text-[11px] normal-case hover:bg-[hsl(var(--primary))]/15" title="Address Book">
              <MapPin className="h-3.5 w-3.5" /> Address Book
            </button>
          </div>
          <div className="space-y-4 p-4">
            {shipOrigins.length > 0 && (
              <Field label="Ship From (PHS Location)">
                <Select value={form.ship_origin_id || ""} onValueChange={selectShipFrom}>
                  <SelectTrigger className="h-9" data-testid="ltl-ship-origin">
                    <SelectValue placeholder="Select PHS location (sets origin + FedEx account)" />
                  </SelectTrigger>
                  <SelectContent>
                    {shipOrigins.map((o) => (
                      <SelectItem key={o.id} value={o.id} data-testid={`ltl-ship-origin-${o.id}`}>
                        {o.label}{o.city ? ` — ${o.city}, ${o.state} ${o.zip}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Pickup Date">
              <Input data-testid="ltl-pickup-date" type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className="h-9" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pickup ZIP">
                <Input data-testid="ltl-origin-zip" value={form.origin_zip} onChange={(e) => zipFill(e.target.value, { zipKey: "origin_zip", cityKey: "origin_city", stateKey: "origin_state" })} className="h-9" placeholder="60173" />
              </Field>
              <Field label="City / State">
                <div className="flex gap-2">
                  <Input value={form.origin_city} onChange={(e) => set("origin_city", e.target.value)} data-testid="ltl-origin-city" className="h-9" placeholder="City" />
                  <Input value={form.origin_state} onChange={(e) => set("origin_state", e.target.value)} data-testid="ltl-origin-state" className="h-9 w-16" placeholder="ST" />
                </div>
              </Field>
            </div>
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Accessorial Services</div>
              <div className="flex flex-wrap gap-2">
                {PICKUP_ACC.map(([k, label]) => (
                  <Chip key={k} testid={`acc-${k}`} active={form.accessorials[k]} onClick={() => toggleAcc(k)} label={label} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Swap */}
        <div className="flex items-center justify-center">
          <Button data-testid="ltl-swap" variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={swapEnds} title="Swap pickup & destination">
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Destination */}
        <div className="overflow-hidden border border-border bg-card">
          <div className="flex items-center justify-between bg-[hsl(var(--success))]/15 px-4 py-2 text-sm font-bold uppercase tracking-wider text-[hsl(var(--success))]">
            <span>Destination</span>
            <button data-testid="ltl-addressbook-destination" onClick={() => setAddrBook({ open: true, side: "destination" })} className="flex items-center gap-1 rounded px-2 py-1 text-[11px] normal-case hover:bg-[hsl(var(--success))]/15" title="Address Book">
              <MapPin className="h-3.5 w-3.5" /> Address Book
            </button>
          </div>
          <div className="space-y-4 p-4">
            <Field label="Delivery Date (optional)">
              <Input data-testid="ltl-delivery-date" type="date" value={form.delivery_date || ""} onChange={(e) => set("delivery_date", e.target.value)} className="h-9" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Destination ZIP">
                <Input data-testid="ltl-dest-zip" value={form.destination_zip} onChange={(e) => zipFill(e.target.value, { zipKey: "destination_zip", cityKey: "dest_city", stateKey: "dest_state" })} className="h-9" placeholder="30301" />
              </Field>
              <Field label="City / State">
                <div className="flex gap-2">
                  <Input value={form.dest_city} onChange={(e) => set("dest_city", e.target.value)} data-testid="ltl-dest-city" className="h-9" placeholder="City" />
                  <Input value={form.dest_state} onChange={(e) => set("dest_state", e.target.value)} data-testid="ltl-dest-state" className="h-9 w-16" placeholder="ST" />
                </div>
              </Field>
            </div>
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Accessorial Services</div>
              <div className="flex flex-wrap gap-2">
                {DEST_ACC.map(([k, label]) => (
                  <Chip key={k} testid={`acc-${k}`} active={form.accessorials[k]} onClick={() => toggleAcc(k)} label={label} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <RouteMap originZip={form.origin_zip} destZip={form.destination_zip} pickupDate={form.pickup_date} />

      {/* Items */}
      <div className="border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-heading text-base font-bold tracking-tight">Items</h2>
          <span className="text-xs text-muted-foreground">{items.length} line item(s)</span>
        </div>
        <div className="divide-y divide-border">
          {items.map((it, idx) => {
            const product = products.find((p) => p.id === it.product_id);
            return (
              <div key={it._key} className="p-4" data-testid={`ltl-item-${idx}`}>
                {/* Item toolbar */}
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <div className="inline-flex overflow-hidden rounded-md border border-border">
                    <button
                      data-testid={`ltl-item-${idx}-mode-catalog`}
                      onClick={() => updateItem(it._key, { mode: "catalog" })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${it.mode === "catalog" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"}`}
                    >
                      <BookOpen className="h-3.5 w-3.5" /> Catalog
                    </button>
                    <button
                      data-testid={`ltl-item-${idx}-mode-manual`}
                      onClick={() => updateItem(it._key, { mode: "manual" })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${it.mode === "manual" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"}`}
                    >
                      <Package className="h-3.5 w-3.5" /> Manual
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    {["stackable", "hazmat", "used", "machinery"].map((flag) => (
                      <label key={flag} className="flex cursor-pointer items-center gap-1.5 capitalize text-muted-foreground">
                        <input
                          type="checkbox"
                          data-testid={`ltl-item-${idx}-${flag}`}
                          checked={it[flag]}
                          onChange={(e) => updateItem(it._key, { [flag]: e.target.checked })}
                          className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                        />
                        {flag}
                      </label>
                    ))}
                  </div>
                  {it.mode === "manual" && (
                    <label className="ml-auto flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Total Wgt
                      <Switch data-testid={`ltl-item-${idx}-totalwgt`} checked={it.weight_is_total} onCheckedChange={(v) => updateItem(it._key, { weight_is_total: v })} />
                    </label>
                  )}
                  <button
                    data-testid={`ltl-item-${idx}-delete`}
                    onClick={() => removeItem(it._key)}
                    disabled={items.length === 1}
                    className="text-[hsl(var(--destructive))] disabled:opacity-30"
                    title="Remove item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Item fields */}
                {it.mode === "catalog" ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                    <Field label="Product" className="col-span-2 lg:col-span-3">
                      <Select value={it.product_id} onValueChange={(v) => updateItem(it._key, { product_id: v })}>
                        <SelectTrigger data-testid={`ltl-item-${idx}-product`} className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Quantity (pieces)">
                      <Input data-testid={`ltl-item-${idx}-quantity`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(it._key, { quantity: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="NMFC">
                      <Input value={it.nmfc} onChange={(e) => updateItem(it._key, { nmfc: e.target.value })} className="h-9" placeholder="optional" />
                    </Field>
                    {product && (
                      <div className="col-span-2 flex items-end text-xs text-muted-foreground lg:col-span-6">
                        <span>Auto-stacked from catalog: {num(product.length)}×{num(product.width)}×{num(product.height)} in · {num(product.weight)} lb/pc · stack engine computes handling units &amp; class on Get Rates.</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                    <Field label="Units">
                      <Input data-testid={`ltl-item-${idx}-units`} type="number" min="1" value={it.units} onChange={(e) => updateItem(it._key, { units: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="Handling Unit">
                      <Select value={it.handling_unit_type} onValueChange={(v) => updateItem(it._key, { handling_unit_type: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{HANDLING_UNITS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Pieces">
                      <Input type="number" min="1" value={it.pieces} onChange={(e) => updateItem(it._key, { pieces: e.target.value })} className="h-9" />
                    </Field>
                    <Field label={it.weight_is_total ? "Weight (total)" : "Weight (per unit)"}>
                      <Input data-testid={`ltl-item-${idx}-weight`} type="number" value={it.weight} onChange={(e) => updateItem(it._key, { weight: e.target.value })} className="h-9" placeholder="lbs" />
                    </Field>
                    <Field label="Length (in)">
                      <Input data-testid={`ltl-item-${idx}-length`} type="number" value={it.length} onChange={(e) => updateItem(it._key, { length: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="Width (in)">
                      <Input data-testid={`ltl-item-${idx}-width`} type="number" value={it.width} onChange={(e) => updateItem(it._key, { width: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="Height (in)">
                      <Input data-testid={`ltl-item-${idx}-height`} type="number" value={it.height} onChange={(e) => updateItem(it._key, { height: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="Class">
                      <Select value={it.freight_class} onValueChange={(v) => updateItem(it._key, { freight_class: v })}>
                        <SelectTrigger data-testid={`ltl-item-${idx}-class`} className="h-9"><SelectValue placeholder="auto" /></SelectTrigger>
                        <SelectContent className="max-h-72">{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="NMFC">
                      <Input value={it.nmfc} onChange={(e) => updateItem(it._key, { nmfc: e.target.value })} className="h-9" />
                    </Field>
                    <Field label="Description" className="col-span-2 sm:col-span-3 lg:col-span-7">
                      <Input data-testid={`ltl-item-${idx}-description`} value={it.description} onChange={(e) => updateItem(it._key, { description: e.target.value })} className="h-9" placeholder="Commodity description" />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Items footer */}
        <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-6 text-sm">
            <div><span className="text-muted-foreground">Total units</span> <span className="ml-1 font-bold tabular-nums">{num(result?.totals?.total_units ?? localTotals.units)}</span></div>
            <div><span className="text-muted-foreground">Total weight</span> <span className="ml-1 font-bold tabular-nums">{num(result?.totals?.total_weight ?? localTotals.weight)} lb</span></div>
            {result?.totals && <div><span className="text-muted-foreground">Linear feet</span> <span className="ml-1 font-bold tabular-nums">{num(result.totals.total_linear_feet, 1)} ft</span></div>}
            {result?.totals?.rating_class && <div><span className="text-muted-foreground">Rating class</span> <span className="ml-1 font-bold tabular-nums">{result.totals.rating_class}</span></div>}
          </div>
          <div className="flex gap-2">
            <Button data-testid="ltl-add-manual" variant="outline" size="sm" onClick={() => addItem("manual")}><Plus className="mr-1 h-4 w-4" /> Add Item</Button>
            <Button data-testid="ltl-add-catalog" variant="outline" size="sm" onClick={() => addItem("catalog")}><BookOpen className="mr-1 h-4 w-4" /> Add From Catalog</Button>
          </div>
        </div>
      </div>

      {/* Get Rates */}
      <div className="flex justify-end">
        <Button data-testid="ltl-get-rates" size="lg" onClick={getRates} disabled={loading} className="gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
          Get Rates
        </Button>
      </div>

      {/* Warnings */}
      {result && (errors.length > 0 || warns.length > 0) && (
        <div className="space-y-1.5">
          {errors.map((w, i) => (
            <div key={`e${i}`} className="flex items-center gap-2 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-[hsl(var(--destructive))]">
              <AlertTriangle className="h-4 w-4" />{w.message}
            </div>
          ))}
          {warns.map((w, i) => (
            <div key={`w${i}`} className="flex items-center gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-sm text-[hsl(var(--warning))]">
              <AlertTriangle className="h-4 w-4" />{w.message}
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          <ExecutiveOverview form={form} productName={`${result.line_items.length} item(s)`} result={result} financial={financial} selectedRate={selectedRate} />

          <CarrierComparison
            result={result}
            selectedCode={selectedCode}
            onSelect={setSelectedCode}
            manualRate={manualRate}
            onManualRate={setManualRate}
            variant="command"
          />

          <LineItemsBreakdown items={result.line_items} totals={result.totals} fak={result.fak} />

          <FinancialBreakdown
            result={result}
            selectedRate={selectedRate}
            financial={financial}
            onApplyOverride={(price, reason) => setOverride({ price, reason })}
            onClearOverride={() => setOverride({ price: null, reason: "" })}
            onSave={saveQuote}
            onDuplicate={duplicateQuote}
            onCustomer={() => setShowCustomer(true)}
            onDiagnostics={() => setShowDiag(true)}
            onExplain={explain}
            saving={saving}
            savedQuoteId={savedQuote?.id}
            savedQuoteNumber={savedQuote?.quote_number}
          />

          {savedQuote && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="mr-auto text-sm text-muted-foreground" data-testid="ltl-saved-ref">
                Saved as <span className="font-bold text-foreground">{savedQuote.quote_number}</span>
              </span>
              <Button data-testid="ltl-download-pdf" variant="outline" onClick={downloadPdf} className="gap-2"><FileDown className="h-4 w-4" /> PDF</Button>
              <Button data-testid="ltl-email" onClick={() => setShowEmail(true)} className="gap-2"><Mail className="h-4 w-4" /> Email Quote</Button>
            </div>
          )}
        </>
      )}

      <EmailQuoteDialog open={showEmail} onOpenChange={setShowEmail} quote={savedQuote} />
      <CustomerSummary open={showCustomer} onOpenChange={setShowCustomer} form={form} result={result} financial={financial} />
      <DiagnosticsDrawer open={showDiag} onOpenChange={setShowDiag} result={result} selectedRate={selectedRate} />
      <Dialog open={showExplain} onOpenChange={setShowExplain}>
        <DialogContent className="max-w-lg" data-testid="ltl-explain-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" /> Quote Explanation</DialogTitle>
            <DialogDescription>Plain-English summary of how this rate was built.</DialogDescription>
          </DialogHeader>
          {explaining ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</div>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="ltl-explain-text">{explainText}</p>
          )}
        </DialogContent>
      </Dialog>
      <AddressBookDialog
        open={addrBook.open}
        onOpenChange={(v) => setAddrBook((a) => ({ ...a, open: v }))}
        side={addrBook.side}
        prefill={addrBook.open ? addrPrefill() : null}
        onSelect={applyAddress}
      />
    </div>
  );
}

function LineItemsBreakdown({ items = [], totals = {}, fak }) {
  if (!items.length) return null;
  return (
    <div className="border border-border bg-card" data-testid="ltl-line-breakdown">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-heading text-base font-bold tracking-tight">Shipment Breakdown</h3>
        <span className="label-caps">{items.length} line(s) · rating class {totals.rating_class || "—"}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps text-left text-muted-foreground">
              <th className="px-4 py-2 font-semibold">#</th>
              <th className="px-2 py-2 font-semibold">Description</th>
              <th className="px-2 py-2 text-right font-semibold">Units</th>
              <th className="px-2 py-2 text-right font-semibold">Pieces</th>
              <th className="px-2 py-2 text-right font-semibold">Weight</th>
              <th className="px-2 py-2 text-right font-semibold">Dims (in)</th>
              <th className="px-2 py-2 text-right font-semibold">Density</th>
              <th className="px-2 py-2 text-right font-semibold">Class</th>
              <th className="px-4 py-2 text-right font-semibold">Lin ft</th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, i) => (
              <tr key={i} className="border-t border-border" data-testid={`ltl-breakdown-row-${i}`}>
                <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-2 font-medium">
                  {li.description || li.product_name}
                  {li.mode === "catalog" && <span className="ml-1 rounded bg-secondary px-1 text-[10px] uppercase text-muted-foreground">catalog</span>}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{num(li.units)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{num(li.pieces)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{num(li.weight)} lb</td>
                <td className="px-2 py-2 text-right tabular-nums">{li.length ? `${num(li.length)}×${num(li.width)}×${num(li.height)}` : "—"}</td>
                <td className="px-2 py-2 text-right tabular-nums">{li.density ? num(li.density, 2) : "—"}</td>
                <td className="px-2 py-2 text-right">
                  <span className="font-bold">{li.freight_class || "—"}</span>
                  {li.fak_applied && li.fak_class !== li.freight_class && <span className="ml-1 text-xs text-[hsl(var(--primary))]">→{li.fak_class}</span>}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{num(li.linear_feet, 1)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-secondary/40 font-bold">
              <td className="px-4 py-2" colSpan={2}>Totals</td>
              <td className="px-2 py-2 text-right tabular-nums">{num(totals.total_units)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{num(totals.total_pieces)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{num(totals.total_weight)} lb</td>
              <td></td>
              <td></td>
              <td className="px-2 py-2 text-right">{totals.rating_class || "—"}</td>
              <td className="px-4 py-2 text-right tabular-nums">{num(totals.total_linear_feet, 1)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {fak?.applied && <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{fak.reason}</div>}
    </div>
  );
}
